<template>
  <div class="gm-page dashboard">
    <div class="gm-page__body">
    <!-- Left Full Height Section -->
    <ActionLog
      class="left-section gm-panel gm-scroll"
      :actions="actions"
      :gmMode="true"
      :characters="characters"
      :npc-characters="npcCharacters"
      @refresh="refreshActions"
      @characterSelected="selectCharacter"
    />
    <!-- Center Full Height Section -->
    <div class="center-section gm-panel">
      <div class="center-cards" v-if="(characters && characters.length > 0) || (npcCharacters && npcCharacters.length > 0)">
        <CharacterCardHolder
            v-if="characters && characters.length > 0"
            :characters="characters"
            :selectedCharacterId="selectedCharacterId"
            title="Players here"
            class="card-holder"
            :additional-characters-data="additionalCharactersData"
            @characterSelected="selectCharacter"
            @characterDeselected="deselectCharacter"
        />
        <CharacterCardHolder
            v-if="npcCharacters && npcCharacters.length > 0"
            :characters="npcCharacters"
            :selectedCharacterId="selectedCharacterId"
            title="NPCs here"
            :additional-characters-data="additionalCharactersData"
            @characterSelected="selectCharacter"
            @characterDeselected="deselectCharacter"
            class="card-holder"
        />
      </div>
      <DynamicBackground
          class="stage"
          fit="cover"
          :backgroundUrl="selectedCharacterPositionBackground"
          v-if="selectedCharacterData && selectedCharacterInfo">
        <div class="stage-nameplate">
          <span class="stage-nameplate__name">{{ selectedCharacterData.name }}</span>
          <button type="button" class="gm-btn gm-btn--ghost gm-btn--sm stage-nameplate__clear"
                  title="Deselect character" @click="deselectCharacter">
            Deselect
          </button>
        </div>
        <div
              class="character-bars"
          >
            <CharacterRPGBars

                :character="{
                  name: selectedCharacterData?.name || 'Unknown',
                  level: selectedCharacterData?.rank_grade || 0,
                  health: selectedCharacterData?.attributes.find(attr => attr.name === 'Health')?.current || 0,
                  maxHealth: selectedCharacterData?.attributes.find(attr => attr.name === 'Health')?.max || 100,
                  flow: selectedCharacterData?.attributes.find(attr => attr.name === 'Energy')?.current || 0,
                  maxFlow: selectedCharacterData?.attributes.find(attr => attr.name === 'Energy')?.max || 100,
                  actionPoints: selectedCharacterData?.attributes.find(attr => attr.name === 'Action Points')?.current || 0,
                  maxActionPoints: selectedCharacterData?.attributes.find(attr => attr.name === 'Action Points')?.max || 10,
                  avatar: selectedCharacterInfo?.biography?.avatar || '',
                  rank_grade: selectedCharacterData?.rank_grade || 0,
                  dimension: selectedCharacterData?.dimension || 0,
                  id: selectedCharacterData?.id || '',
                  position: selectedCharacterData?.position || '',
                  tags: selectedCharacterData?.tags || []
                }"
                :shields="shields"
                :effects="activeEffects"
                :reset-base-stats="false"
                @openInfo="openInfo"
            />
            <GameMasterCharacterInfo :character-data="selectedCharacterData" @open-teleport="toggleTeleportComponent" @open-action-constructor="openSkillCreationModal" @open-dice-roll-challenge="openDiceChallengeModal" />
          </div>
        <TeleportComponent v-if="showTeleportComponent" @teleportToCoordinates="teleportToCoordinates" @teleportToPosition="teleportToPosition"/>
        <div class="stage-hud">
          <CustomAction
              :initiator="selectedInitiator"
              :target="selectedTarget"
              :action="selectedAction"
              @selectInitiator="handleSelectInitiator"
              @selectTarget="handleSelectTarget"
              @selectAction="handleSelectAction"
              @performAction="handlePerformAction"
              @cancelAction="handleCancelAction"
          />
        </div>
      </DynamicBackground>
      <div v-else-if="selectedCharacterId" class="stage gm-empty">
        <span class="gm-empty__title">Loading character…</span>
        <span class="gm-empty__hint">Fetching their location, vitals and effects.</span>
      </div>
      <GmCharacterPicker
          v-else
          class="picker-stage"
          :characters="rosterCharacters"
          :vitals="additionalCharactersData"
          :selected-id="selectedCharacterId"
          @select="selectCharacter"
      />
    </div>
    <!-- Right Full Height Section -->
    <div class="right-section gm-panel">
      <GmCycleStatus :current-turn="currentCycleNumber" :current-phase="currentCyclePhase"
                     :switching="cycleSwitching" class="current-turn"/>

      <div class="right-section__scroll gm-scroll">
        <GameObjectRawSelector @gameObjectSelected="selectLocationId" item-name="Position"
                               placeholder="Enter Position UUID"/>
        <GameObjectRawSelector @gameObjectSelected="selectCharacter" item-name="Character"
                               placeholder="Enter Character UUID"/>

        <hr class="gm-divider"/>

        <!-- Fight Manager Section -->
        <GameMasterFightManager
          :selectedCharacterData="selectedCharacterData"
        />
      </div>

      <EndTurnComponent class="end-turn"
                        @switch-start="beginCycleSwitch"
                        @switch-end="endCycleSwitch"/>
    </div>
    </div>

    <!-- Character Selector Modal -->
    <div v-if="showCharSelector" class="modal-overlay" @click="handleCharSelectorClose">
      <div class="modal-container" @click.stop>
        <GmCharSelector
            :characters="characters.concat(npcCharacters)"
            @select="handleCharacterSelected"
            @close="handleCharSelectorClose"
        />
      </div>
    </div>

    <!-- Skill Selector Modal -->
    <div v-if="showSkillSelector" class="modal-overlay" @click="handleSkillSelectorClose">
      <div class="modal-container" @click.stop>
        <GMRPGSkills
            :isDraggable="false"
            @skillSelected="handleSkillSelected"
            @close="handleSkillSelectorClose"
        />
      </div>
    </div>

    <!-- Character Card Modal -->
    <div v-if="showCharacterCard && selectedCharacterId" class="modal-overlay" @click="closeCharacterCard">
      <div class="modal-container" @click.stop>
        <GameMasterCharacterCard
            :characterId="selectedCharacterId"
            @close="closeCharacterCard"
        />
      </div>
    </div>

    <!-- Skill Creation Modal -->
    <SkillCreationModal
        :isOpen="showSkillCreationModal"
        @close="closeSkillCreationModal"
        @skillCreated="handleSkillCreated"
    />

    <!-- Dice Challenge Modal -->
    <div v-if="showDiceChallengeModal && selectedCharacterId" class="modal-overlay" @click="closeDiceChallengeModal">
      <div class="modal-container" @click.stop>
        <GameMasterDiceChallenge
            :selectedCharacterId="selectedCharacterId"
            @close="closeDiceChallengeModal"
            @challengeCreated="handleChallengeCreated"
        />
      </div>
    </div>
  </div>
</template>


<script lang="ts">
import EndTurnComponent from "@/components/GameMaster/EndTurnComponent.vue";
import GmCycleStatus from "@/components/GameMaster/GmCycleStatus.vue";
import ActionLog from "@/components/GameMaster/ActionLog/ActionLogComponent.vue";
import {ActionGameApi, CharacterGameApi, ShieldsGameApi, EffectsGameApi} from "@/api/backendService.js";
import CharacterCardHolder from "@/components/GameMaster/Character/CharacterCardHolder.vue";
import GameObjectRawSelector from "@/components/GameMaster/GameObjectRawSelector.vue";
import BackgroundView from "@/components/Game/Location/BackgroundView.vue";
import {LocationInfoGameService} from "@/services/locationInfoService";
import DynamicBackground from "@/components/Background/DynamicBackground.vue";
import GmCharacterPicker from "@/components/GameMaster/Character/GmCharacterPicker.vue";
import TeleportComponent from "@/components/GameMaster/TeleportComponent.vue";
import {ensureConnection} from "@/api/dx-websocket/index.ts";
import CustomAction from "@/components/GameMaster/CustomAction/CustomAction.vue";
import GmCharSelector from "@/components/GameMaster/Character/GMCharSelector.vue";
import GMRPGSkills from "@/components/GameMaster/RPGSkills/GMRPGSkills.vue";
import CharacterRPGBars from "@/components/PlayerRPGBars/CharacterRPGBars.vue";
import GameMasterTools from "@/components/GameMaster/GameMasterTools.vue";
import GameMasterCharacterCard from "@/components/WorldEditor/GameMasterCharacterCard.vue";
import SkillCreationModal from "@/components/GameMaster/SkillFactory/SkillCreationModal.vue";
import GameMasterDiceChallenge from "@/components/GameMaster/Character/GameMasterDiceChallenge.vue";
import GameMasterFightManager from "@/components/GameMaster/GameMasterFightManager.vue";
import type {
  GameMasterCharacterActionLog,
  CharacterOnPosition,
  GameMasterCharacterInfo,
  CharacterInfo,
  Shield,
  Effect,
  Cycle,
  GameMasterCharacterActionRequest,
  CharacterActionTypeEnum
} from "@/api/dx-backend";
import type { DefineComponent } from 'vue';

// Local interfaces for component-specific types
interface ActionParticipant {
  id: string;
  name: string;
  imageUrl?: string;
}

interface CustomActionData {
  skillId: string;
  name: string;
  description: string;
  school: string;
  grade: number;
  type: string;
}

interface AdditionalCharacterData {
  [characterId: string]: any;
}

interface CycleChangeData {
  id: number;
  is_current: boolean;
  created_at: string;
  updated_at: string;
  number: number;
  phase: string;
  campaign: string;
}

interface CharacterChangeData {
  id: string;
  [key: string]: any;
}

type SelectionType = 'initiator' | 'target' | null;

export default {
  components: {
    CharacterRPGBars,
    CustomAction,
    GmCharSelector,
    GMRPGSkills,
    TeleportComponent,
    GmCharacterPicker,
    DynamicBackground,
    BackgroundView,
    GameObjectRawSelector,
    CharacterCardHolder, ActionLog, GmCycleStatus, EndTurnComponent,
    GameMasterCharacterInfo: GameMasterTools,
    GameMasterCharacterCard,
    SkillCreationModal,
    GameMasterDiceChallenge,
    GameMasterFightManager
  },
  data() {
    return {
      actions: [] as GameMasterCharacterActionLog[],
      activePlayersCharacters: [] as CharacterOnPosition[],
      activeNpcCharacters: [] as CharacterOnPosition[],
      characters: [] as CharacterOnPosition[],
      npcCharacters: [] as CharacterOnPosition[],
      selectedLocationId: null as string | null,
      selectedCharacterId: null as string | null,
      selectedCharacterPositionBackground: null as string | null,
      pendingBackgroundPositionId: null as string | null,
      selectedCharacterInfo: null as CharacterInfo | null,
      selectedCharacterData: null as GameMasterCharacterInfo | null,
      locationGMService: LocationInfoGameService,
      selectedCharacterShields: [] as Shield[],
      shields: [] as Shield[],
      activeEffects: [] as Effect[],
      currentCycleNumber: null as number | null,
      currentCyclePhase: null as string | null,
      /* A switch has two independent owners — this GM's own request and the
         cycle arriving over the socket — so it is counted, not flagged. */
      cycleSwitchDepth: 0 as number,
      additionalCharactersData: {} as AdditionalCharacterData,
      selectedInitiator: undefined as ActionParticipant | undefined,
      selectedTarget: undefined as ActionParticipant | undefined,
      selectedAction: undefined as CustomActionData | undefined,
      showCharSelector: false as boolean,
      showSkillSelector: false as boolean,
      showCharacterCard: false as boolean,
      showTeleportComponent: false as boolean,
      showSkillCreationModal: false as boolean,
      showDiceChallengeModal: false as boolean,
      currentSelectionType: null as SelectionType,
      bus: null as any
    };
  },
  async mounted() {
    /* Subscribed before the initial loads, because a single failing panel used to
       reject the batch below and leave the whole dashboard unsubscribed from the world. */
    this.bus = ensureConnection();
    this.bus.on("world::new_cycle", this.handleCycleChange);
    this.bus.on("world::action_accepted", this.handleNewAction);
    this.bus.on("world::action_performed", this.handleNewAction);
    this.bus.on("world::character_changed", this.handleCharacterChange);

    /* Nothing here depends on anything else here, and running them in sequence made
       the dashboard sit empty for the sum of all five round trips. One panel failing
       to load must leave the others populated, so these settle independently. */
    const results = await Promise.allSettled([
      this.refresh(),
      this.refreshActions(),
      this.refreshCharacters(),
      this.refreshActivePlayersCharacters(),
      this.refreshActiveNpcCharacters(),
      this.refreshCharactersData(),
      ActionGameApi.actionCurrentCycleRetrieve(),
    ]);
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`GM dashboard load step ${index} failed:`, result.reason);
      }
    });
    const currentCycle = results[6];
    if (currentCycle.status === "fulfilled") {
      this.currentCycleNumber = currentCycle.value.data.number;
      this.currentCyclePhase = currentCycle.value.data.phase;
    }
  },
  async beforeUnmount() {
    // Unsubscribe to prevent memory leaks
    this.bus.off("world::new_cycle", this.handleCycleChange);
    this.bus.off("world::action_accepted", this.handleNewAction);
    this.bus.off("world::action_performed", this.handleNewAction);
    this.bus.off("world::character_changed", this.handleCharacterChange);
  },
  methods: {
    beginCycleSwitch(): void {
      this.cycleSwitchDepth += 1;
    },
    endCycleSwitch(): void {
      this.cycleSwitchDepth = Math.max(0, this.cycleSwitchDepth - 1);
    },
    async handleCharacterChange(data: CharacterChangeData): Promise<void> {
      console.debug("Character Changed:", {data});
      this.additionalCharactersData[data.id] = data;
    },
    async handleNewAction(data: GameMasterCharacterActionLog): Promise<void> {
      console.debug("New Action Accepted:", {data});
      // check if data.id in this.actions then replace it else add it
      const index = this.actions.findIndex((action) => action.id === data.id);
      if (index !== -1) {
        this.actions[index] = data;
      } else {
        this.actions.push(data);
      }
      // re-render the actions and re order them by order
      this.actions = [...this.actions].sort((a, b) => (b.order || 0) - (a.order || 0));
    },
    async handleCycleChange(data: CycleChangeData): Promise<void> {
      console.debug("New Cycle:", {data});

      // Only react to cycles in current campaign
      if (!data.is_current) {
        console.debug("Cycle is not current, skipping update");
        return;
      }

      if (data.number !== this.currentCycleNumber || data.phase !== this.currentCyclePhase) {
        this.currentCycleNumber = data.number;
        this.currentCyclePhase = data.phase;
        /* The board is still showing the previous cycle until these land, so the
           switch is not over when the socket event arrives — it is over here. */
        this.beginCycleSwitch();
        try {
          await Promise.all([
            this.refresh(),
            this.refreshCharactersData(),
            this.refreshActivePlayersCharacters(),
          ]);
        } finally {
          this.endCycleSwitch();
        }
      }
    },
    async refreshSelectedCharacterShields(): Promise<void> {
      if (this.selectedCharacterId) {
        this.shields = (await ShieldsGameApi.shieldsGmActiveList(
            this.selectedCharacterId,
        )).data;
      } else {
        this.shields = [];
      }
    },
    async refreshActiveEffects(): Promise<void> {
      if (this.selectedCharacterId) {
        try {
          this.activeEffects = (await EffectsGameApi.effectsActiveList()).data;
        } catch (error) {
          console.error("Error refreshing active effects:", error);
          this.activeEffects = [];
        }
      } else {
        this.activeEffects = [];
      }
    },
    async teleportToCoordinates(x: number, y: number, z: number): Promise<void> {
      if (!this.selectedCharacterId) return;
      await this.locationGMService.teleportToCoordinates(this.selectedCharacterId, x, y, z);
      await this.refresh()
      await this.refreshCharacterPosition();
      await this.refreshCharacters();
      await this.refreshSelectedCharacterShields();
      await this.refreshActiveEffects();
    },
    async teleportToPosition(id: string): Promise<void> {
      if (!this.selectedCharacterId) return;
      await this.locationGMService.teleportToPosition(this.selectedCharacterId, id);
      await this.refresh()
      await this.refreshCharacterPosition();
      await this.refreshCharacters();
    },
    toggleTeleportComponent(): void {
      this.showTeleportComponent = !this.showTeleportComponent;
    },
    /* A double-click deselects, and its own two clicks are still in flight when
       it does — so a reply that no longer matches the selection is dropped
       rather than resurrecting the character that was just dismissed. */
    async selectCharacter(id: string): Promise<void> {
      this.selectedCharacterId = id;
      const [data, info] = await Promise.all([
        CharacterGameApi.characterGmCharacterInfoRetrieve(id),
        CharacterGameApi.characterGmRetrieve(id),
      ]);
      if (this.selectedCharacterId !== id) return;
      this.selectedCharacterData = data.data;
      this.selectedCharacterInfo = info.data;
      if (this.selectedLocationId !== this.selectedCharacterData.position) {
        await this.selectLocationId(this.selectedCharacterData.position);
      }
    },
    deselectCharacter(): void {
      this.selectedCharacterId = null;
      this.selectedCharacterData = null;
      this.selectedCharacterInfo = null;
      this.selectedCharacterPositionBackground = null;
      this.pendingBackgroundPositionId = null;
      this.showTeleportComponent = false;
      this.resetCustomActionState();
    },
    async selectLocationId(id: string): Promise<void> {
      this.selectedLocationId = id;
    },
    async refresh(): Promise<void> {
      // await this.refreshActions();
      if (this.selectedCharacterId) {
        await this.selectCharacter(this.selectedCharacterId);
      }
    },
    async refreshActions(): Promise<void> {
      const newActions = (await ActionGameApi.actionGmList(
          undefined, 100,
      )).data;
      this.actions = newActions.results;
    },
    async refreshActivePlayersCharacters(): Promise<void> {
      this.activePlayersCharacters = (await CharacterGameApi.characterGmList(
          false,
      )).data;
    },
    /* The picker can search NPCs too, so the whole cast has to be loaded even
       though it opens filtered down to players. */
    async refreshActiveNpcCharacters(): Promise<void> {
      this.activeNpcCharacters = (await CharacterGameApi.characterGmList(
          true,
      )).data;
    },
    /* Card vitals otherwise stay blank until the first character_changed event,
       which can be a whole cycle away. */
    async refreshCharactersData(): Promise<void> {
      try {
        const infos = (await CharacterGameApi.characterGmCharacterInfosList()).data;
        this.additionalCharactersData = Object.fromEntries(
            infos.map((info) => [info.id, info]),
        );
      } catch (error) {
        console.error("Error loading character vitals:", error);
      }
    },
    async refreshCharacters(): Promise<void> {
      this.characters = (await CharacterGameApi.characterGmList(
          false, this.selectedLocationId,
      )).data;
    },
    async refreshNPCharacters(): Promise<void> {
      this.npcCharacters = (await CharacterGameApi.characterGmList(
          true, this.selectedLocationId,
      )).data;
    },
    /* Switching characters must always land on that character's own location.
       Previously a character with no position left the previous one's art on
       screen, and a slow lookup could overwrite a newer selection's result. */
    async refreshCharacterPosition(): Promise<void> {
      const positionId = this.selectedCharacterData?.position;
      if (!positionId) {
        this.selectedCharacterPositionBackground = null;
        return;
      }

      const requestedFor = positionId;
      this.pendingBackgroundPositionId = requestedFor;
      try {
        const background = await this.locationGMService.getBackgroundUrl(positionId);
        if (this.pendingBackgroundPositionId !== requestedFor) return;
        this.selectedCharacterPositionBackground = background;
      } catch (error) {
        console.error(`Error loading background for position ${positionId}:`, error);
        if (this.pendingBackgroundPositionId === requestedFor) {
          this.selectedCharacterPositionBackground = null;
        }
      }
    },
    handleSelectInitiator(): void {
      this.currentSelectionType = 'initiator';
      this.showCharSelector = true;
    },
    handleSelectTarget(): void {
      this.currentSelectionType = 'target';
      this.showCharSelector = true;
    },
    handleSelectAction(): void {
      this.showSkillSelector = true;
    },
    handleCharacterSelected(characterId: string): void {
      const character = this.characters.concat(this.npcCharacters).find(c => c.id === characterId);
      if (character) {
        const participant: ActionParticipant = {
          id: character.id,
          name: character.name,
          imageUrl: character.biography?.avatar
        };

        if (this.currentSelectionType === 'initiator') {
          this.selectedInitiator = participant;
        } else if (this.currentSelectionType === 'target') {
          this.selectedTarget = participant;
        }
      }

      this.showCharSelector = false;
      this.currentSelectionType = null;
    },
    handleSkillSelected(skill: any): void {
      // Transform skill to action format
      const action: CustomActionData = {
        skillId: skill.id || skill.name, // Use skill ID or name as identifier
        name: skill.name,
        description: skill.description,
        school: skill.school,
        grade: skill.grade,
        type: skill.type
      };

      this.selectedAction = action;
      this.showSkillSelector = false;
    },
    handleCharSelectorClose(): void {
      this.showCharSelector = false;
      this.currentSelectionType = null;
    },
    handleSkillSelectorClose(): void {
      this.showSkillSelector = false;
    },
    async handlePerformAction(action: CustomActionData): Promise<void> {
      // Validate required data
      if (!this.selectedInitiator || !this.selectedTarget || !action) {
        console.error('Missing required data for action:', {
          initiator: this.selectedInitiator,
          target: this.selectedTarget,
          action: action
        });
        return;
      }

      try {
        // Create the GameMasterCharacterActionRequest payload
        const actionRequest: GameMasterCharacterActionRequest = {
          initiator: this.selectedInitiator.id,
          targets: [this.selectedTarget.id],
          action_type: 'USE_SKILL' as CharacterActionTypeEnum,
          skill: parseInt(action.skillId) || 0,
        };

        console.log('Performing action:', {
          initiator: this.selectedInitiator,
          target: this.selectedTarget,
          action: action,
          request: actionRequest
        });

        // Call the backend API to register the character action
        const response = await ActionGameApi.actionGmRegisterCharacterActionCreate(actionRequest);

        console.log('Action registered successfully:', response.data);

        // Refresh actions to show the new action
        await this.refreshActions();

      } catch (error) {
        console.error('Error performing action:', error);
        // You could add user notification here if needed
      }
      // Note: We don't reset the custom action state anymore to keep the current hasTarget and action selected
    },
    handleCancelAction(): void {
      // Reset state when canceling
      this.resetCustomActionState();
    },
    resetCustomActionState(): void {
      // Reset all custom action related state
      this.selectedInitiator = undefined;
      this.selectedTarget = undefined;
      this.selectedAction = undefined;
      this.currentSelectionType = null;
    },
    openInfo(): void {
      // Show the character card modal when character bars are clicked
      this.showCharacterCard = true;
    },
    closeCharacterCard(): void {
      // Close the character card modal
      this.showCharacterCard = false;
    },
    openSkillCreationModal(): void {
      // Open the skill creation modal
      this.showSkillCreationModal = true;
    },
    closeSkillCreationModal(): void {
      // Close the skill creation modal
      this.showSkillCreationModal = false;
    },
    handleSkillCreated(skill: any): void {
      // Handle when a skill is successfully created
      console.log('Skill created:', skill);
      // Close the modal after successful creation
      this.showSkillCreationModal = false;
      // You could add additional logic here like refreshing skill lists, showing notifications, etc.
    },
    openDiceChallengeModal(characterData: GameMasterCharacterInfo): void {
      // Open the dice challenge modal
      this.showDiceChallengeModal = true;
    },
    closeDiceChallengeModal(): void {
      // Close the dice challenge modal
      this.showDiceChallengeModal = false;
    },
    handleChallengeCreated(challengeId: string): void {
      // Handle when a challenge is successfully created
      console.log('Challenge created:', challengeId);
      // Close the modal after successful creation
      this.showDiceChallengeModal = false;
      // You could add additional logic here like refreshing action logs, showing notifications, etc.
    },
  },
  computed: {
    cycleSwitching(): boolean {
      return this.cycleSwitchDepth > 0;
    },
    rosterCharacters(): CharacterOnPosition[] {
      return [...this.activePlayersCharacters, ...this.activeNpcCharacters];
    },
  },
  watch: {
    selectedLocationId() {
      this.refreshNPCharacters();
      this.refreshCharacters()
    },
    selectedCharacterId() {
      this.refreshSelectedCharacterShields();
      this.refreshActiveEffects();
    },
    selectedCharacterData() {
      this.refreshCharacterPosition();
    },
  }
};
</script>

<style scoped>
/* The dashboard never scrolls as a page: it is pinned to the shell area left by
   the game header and footer, and every column scrolls inside itself instead. */
.dashboard {
  position: absolute;
  inset: 0;
  overflow: hidden;
  gap: var(--gm-gap);
  padding: var(--gm-gap);
  box-sizing: border-box;
}

.gm-page__body {
  gap: var(--gm-gap);
  padding-bottom: 0;
  min-height: 0;
  overflow: hidden;
}

.picker-stage {
  flex: 1;
  min-height: 0;
}

/* Left Full Height Section */
/* The log owns its top padding so its header can stick flush to the top edge.
   It is a fixed rail rather than a proportional column: its rows carry two
   names and a verb, so extra width only buys whitespace, and every pixel it
   gives back goes to the stage. */
.left-section {
  flex: 0 0 clamp(17rem, 21vw, 22rem);
  min-height: 0;
  padding-top: 0;
  padding-bottom: var(--gm-dock-clearance);
}

.left-section :deep(.action-log-header) {
  padding-top: var(--gm-gap);
}

.center-section {
  flex: 4;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  overflow: hidden;
  padding-bottom: var(--gm-dock-clearance);
}

.center-cards {
  flex: none;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--gm-gap-sm);
}

.card-holder {
  flex: 1;
  min-width: 0;
}

.stage {
  flex: 1;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: var(--gm-radius);
  background-color: var(--gm-surface-sunken);
  overflow: hidden;
  isolation: isolate;
}

/* The location art is a photograph dropped into a panel made of thin gold
   lines. Grading it — scrims where the UI sits, a vignette at the edges —
   keeps the nameplate and the HUD legible over art of any brightness, and
   stops the panel from reading as a cut-out pasted on the dashboard. */
.stage::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background:
      linear-gradient(to bottom, rgba(12, 14, 15, 0.8) 0%, rgba(12, 14, 15, 0) 20%),
      linear-gradient(to top, rgba(12, 14, 15, 0.9) 0%, rgba(12, 14, 15, 0) 40%),
      radial-gradient(125% 95% at 50% 45%, rgba(12, 14, 15, 0) 42%, rgba(12, 14, 15, 0.72) 100%);
}

/* The panel's own edge, drawn over the art so the border never disappears
   into a bright frame of the background. */
.stage::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px var(--gm-border-dim);
}

.stage > * {
  position: relative;
  z-index: 1;
}

.stage-nameplate {
  flex: none;
  display: flex;
  align-items: baseline;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-xs) var(--gm-gap-sm);
  border-radius: var(--gm-radius-sm);
  background: rgba(0, 0, 0, 0.55);
  align-self: flex-start;
  margin: var(--gm-gap-sm);
}

.stage-nameplate__clear {
  padding: 0.1rem 0.45rem;
}

.stage-nameplate__name {
  font-family: var(--gm-font-display);
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--gm-accent);
  text-shadow: 1px 1px 2px #000;
}

.stage-hud {
  margin-top: auto;
  flex: none;
}

/* Right Full Height Section — turn chip and End Turn stay in flow so they never
   cover the panels between them. */
.right-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  overflow: hidden;
  padding-bottom: var(--gm-dock-clearance);
}

.right-section__scroll {
  flex: 1;
  min-height: 0;
}

.current-turn {
  flex: none;
}

.end-turn {
  flex: none;
  padding-top: var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
}

.end-turn :deep(.end-turn-button) {
  width: 100%;
}

/* Modal container styles - invisible positioning containers */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.modal-container {
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
  border-radius: var(--gm-radius);
  box-shadow: var(--gm-shadow);
}

.modal-container::-webkit-scrollbar {
  width: 8px;
}

.modal-container::-webkit-scrollbar-track {
  background: transparent;
}

.modal-container::-webkit-scrollbar-thumb {
  background: rgba(250, 218, 149, 0.3);
  border-radius: 4px;
}

.modal-container::-webkit-scrollbar-thumb:hover {
  background: rgba(250, 218, 149, 0.5);
}

.character-bars {
  scale: 0.75;
  flex: none;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  position: relative;
}
</style>
